import type { BoggleRunSummaryScore } from '~/boggle/config/runSummaryScore'
import type { BoggleGameState, BoggleRunLedger } from '~/boggle/engine/standard/types'
import {
  BOGGLE_SPECIAL_TILE_KINDS,
  getEffectiveGoldCount,
  getVirtualGoldCount,
  type BoggleSpecialTileKind,
} from '~/boggle/engine/specialTiles'
import type { BogglePlaygroundSettings } from '~/boggle/config/playground'
import {
  getBurstActiveAtAcceptance,
  getDuringMeterBurstWindow,
  getMeterBurstActiveAfter,
  isMeterGainExcludedForMeterBurst,
} from '~/boggle/lib/ledgerMeterBurstFacts'
import type { BoggleGameModeId, BoggleRunKind } from '~/boggle/gameModes'
import type { BoggleEarnedBadge } from '~/boggle/lib/badges/evaluateRunBadges'
import {
  sumAcceptedWordEffectiveSpecialTileCounts,
  sumAcceptedWordPhysicalSpecialTileCounts,
} from './acceptedWordFacts'
import {
  BOGGLE_COMPLETED_RUN_FACTS_VERSION,
  BOGGLE_COMPLETED_RUN_SCHEMA_VERSION,
  BOGGLE_LEXICON_VERSION,
  BOGGLE_RULES_VERSION,
  BOGGLE_WORD_METADATA_VERSION,
  type BoggleCompletedRun,
  type BoggleCompletedRunAcceptedWordFact,
  type BoggleCompletedRunActionFact,
  type BoggleCompletedRunEndFact,
  type BoggleCompletedRunSetupSnapshot,
  type BoggleCompletedRunSummarySnapshot,
} from './types'

let fallbackAttemptSequence = 0

export type BoggleRunAttemptIdentity = {
  id: string
  setupSnapshot?: BoggleCompletedRunSetupSnapshot
  startedAt: string
}

export function createBoggleRunAttemptIdentity(
  now: Date = new Date(),
  setupSnapshot?: BoggleCompletedRunSetupSnapshot,
): BoggleRunAttemptIdentity {
  const randomUuid = globalThis.crypto?.randomUUID?.()
  fallbackAttemptSequence += 1

  return {
    id: randomUuid ?? `boggle-run-${now.getTime()}-${fallbackAttemptSequence}`,
    setupSnapshot,
    startedAt: now.toISOString(),
  }
}

export function createBoggleCompletedRunSetupSnapshot(
  state: BoggleGameState,
  {
    challenge = null,
    gameModeCorePreset,
    seedInput,
  }: {
    challenge?: BoggleCompletedRunSetupSnapshot['challenge']
    gameModeCorePreset: BogglePlaygroundSettings
    seedInput: number | string
  },
): BoggleCompletedRunSetupSnapshot {
  return {
    boardCols: state.cols,
    boardRows: state.rows,
    challenge: challenge ? cloneJsonLike(challenge) : null,
    gameModeCorePreset: cloneJsonLike(gameModeCorePreset),
    openingBoard: state.board.map(row =>
      row.map(cell =>
        cell
          ? {
              id: cell.id,
              letter: cell.letter,
              tags: cell.tags ? [...cell.tags] : undefined,
            }
          : null,
      ),
    ),
    resolvedPlaygroundSettings: cloneJsonLike(state.playgroundSettings),
    seed: state.seed,
    seedInput,
  }
}

export function createBoggleCompletedRunRecord({
  attempt,
  completedAt,
  earnedBadges,
  gameModeId,
  runLedger,
  runKind,
  summaryScore,
  submittedWords,
}: {
  attempt: BoggleRunAttemptIdentity
  completedAt: string
  earnedBadges: readonly BoggleEarnedBadge[]
  gameModeId: BoggleGameModeId
  runLedger: BoggleRunLedger
  runKind: BoggleRunKind
  summaryScore: BoggleRunSummaryScore
  submittedWords: readonly string[]
}): BoggleCompletedRun | null {
  if (runKind === 'challenge-practice') {
    return null
  }

  const acceptedWordTimeline = createBoggleCompletedRunAcceptedWordTimeline(runLedger)
  assertSubmittedWordsMatchAcceptedWordTimeline(submittedWords, acceptedWordTimeline)

  return {
    acceptedWordTimeline,
    actionTimeline: createBoggleCompletedRunActionTimeline(runLedger),
    completedAt,
    earnedBadges: earnedBadges.map(earned => ({ ...earned })),
    gameModeId,
    id: attempt.id,
    lexiconVersion: BOGGLE_LEXICON_VERSION,
    ratingId: summaryScore.rating.id,
    runFactsVersion: BOGGLE_COMPLETED_RUN_FACTS_VERSION,
    runEnd: createBoggleCompletedRunEndFact(runLedger),
    rulesVersion: BOGGLE_RULES_VERSION,
    runKind,
    schemaVersion: BOGGLE_COMPLETED_RUN_SCHEMA_VERSION,
    setupSnapshot: attempt.setupSnapshot,
    startedAt: attempt.startedAt,
    submittedWords: [...submittedWords],
    summarySnapshot: createBoggleCompletedRunSummarySnapshot(
      summaryScore,
      acceptedWordTimeline,
    ),
    wordMetadataVersion: BOGGLE_WORD_METADATA_VERSION,
  }
}

export function createBoggleCompletedRunSummarySnapshot(
  summaryScore: BoggleRunSummaryScore,
  acceptedWordTimeline: readonly BoggleCompletedRunAcceptedWordFact[],
): BoggleCompletedRunSummarySnapshot {
  const effectiveSpecialTileCounts =
    sumAcceptedWordEffectiveSpecialTileCounts(acceptedWordTimeline)
  const physicalSpecialTileCounts =
    sumAcceptedWordPhysicalSpecialTileCounts(acceptedWordTimeline)

  return {
    effectiveSpecialTileCounts: { ...effectiveSpecialTileCounts },
    goldTilesPlayed: getEffectiveGoldCount(effectiveSpecialTileCounts),
    physicalGoldTilesPlayed: getEffectiveGoldCount(physicalSpecialTileCounts),
    physicalSpecialTileCounts: { ...physicalSpecialTileCounts },
    tierOutcomes: summaryScore.tierLines.map(line => ({
      categoryId: line.category.id,
      metricValue: line.metricValue,
      tierLevel: line.tier?.level ?? null,
    })),
    total: summaryScore.total,
    totalMeterEarned: sumAcceptedWordFacts(acceptedWordTimeline, word => word.meterGain),
    totalOvermeterEarned: sumAcceptedWordFacts(
      acceptedWordTimeline,
      word => word.overmeterGain,
    ),
    uncommonLettersPlayed: sumAcceptedWordFacts(
      acceptedWordTimeline,
      word => word.uncommonLetterCount,
    ),
    virtualGoldTilesPlayed: getVirtualGoldCount(
      effectiveSpecialTileCounts,
      physicalSpecialTileCounts,
    ),
  }
}

export function createBoggleCompletedRunAcceptedWordTimeline(
  runLedger: BoggleRunLedger,
): readonly BoggleCompletedRunAcceptedWordFact[] {
  return runLedger
    .filter(entry => entry.kind === 'accepted-word')
    .map(entry => ({
      acceptedDuringOvertime: entry.acceptedDuringOvertime,
      acceptedAtMs: entry.atMs,
      boardCols: entry.boardCols,
      boardRows: entry.boardRows,
      clearedPositions: entry.clearedPositions.map(position => ({ ...position })),
      duringMeterBurstWindow: getDuringMeterBurstWindow(entry),
      effectiveSpecialTileCountsUsed: { ...entry.effectiveSpecialTileCountsUsed },
      burstActiveAtAcceptance: getBurstActiveAtAcceptance(entry),
      ledgerIndex: entry.index,
      meterGain: entry.meterGain,
      meterBurstActiveAfter: getMeterBurstActiveAfter(entry),
      overtimeSurvivingSelectionCount: entry.overtimeSurvivingSelectionCount,
      overmeterGain: entry.overmeterGain,
      path: entry.path.map(position => ({ ...position })),
      pathSteps: entry.pathSteps.map(step => ({
        effectiveSpecialKinds: [...step.effectiveSpecialKinds],
        letter: step.letter,
        physicalSpecialKinds: [...step.physicalSpecialKinds],
        position: { ...step.position },
        virtualSpecialKinds: [...step.virtualSpecialKinds],
      })),
      physicalSpecialTileCountsUsed: { ...entry.physicalSpecialTileCountsUsed },
      tileLetters: [...entry.tileLetters],
      uncommonLetterCount: entry.uncommonLetterCount,
      uniqueTilesCleared: entry.uniqueTilesCleared,
      word: entry.word,
    }))
}

export function createBoggleCompletedRunEndFact(
  runLedger: BoggleRunLedger,
): BoggleCompletedRunEndFact | undefined {
  for (let index = runLedger.length - 1; index >= 0; index -= 1) {
    const runEnd = runLedger[index]
    if (!runEnd || runEnd.kind !== 'run-end') {
      continue
    }

    return {
      acceptedWordCount: runEnd.acceptedWordCount,
      endedAtMs: runEnd.atMs,
      endReason: runEnd.endReason,
      ledgerIndex: runEnd.index,
      sumAcceptedWordLengths: runEnd.sumAcceptedWordLengths,
    }
  }

  return undefined
}

export function createBoggleCompletedRunActionTimeline(
  runLedger: BoggleRunLedger,
): readonly BoggleCompletedRunActionFact[] {
  return runLedger.flatMap((entry): BoggleCompletedRunActionFact[] => {
    if (entry.kind === 'semantic-action') {
      return entry.actionKind === 'selected-letter'
        ? [
            {
              actionKind: entry.actionKind,
              atMs: entry.atMs,
              ledgerIndex: entry.index,
              letter: entry.letter,
              position: { ...entry.position },
            } satisfies BoggleCompletedRunActionFact,
          ]
        : [
            {
              actionKind: entry.actionKind,
              atMs: entry.atMs,
              ledgerIndex: entry.index,
              word: entry.word,
            } satisfies BoggleCompletedRunActionFact,
          ]
    }

    if (entry.kind === 'correction') {
      return [
        {
          actionKind: entry.correctionKind,
          atMs: entry.atMs,
          ledgerIndex: entry.index,
        } satisfies BoggleCompletedRunActionFact,
      ]
    }

    if (entry.kind === 'phase') {
      return [
        {
          actionKind: 'phase-start',
          atMs: entry.atMs,
          ledgerIndex: entry.index,
          phase: entry.phase,
        } satisfies BoggleCompletedRunActionFact,
      ]
    }

    return []
  })
}

function sumAcceptedWordFacts(
  acceptedWordTimeline: readonly BoggleCompletedRunAcceptedWordFact[],
  getValue: (word: BoggleCompletedRunAcceptedWordFact) => number,
) {
  return acceptedWordTimeline.reduce((sum, word) => sum + getValue(word), 0)
}

/** Meter charge earned before meter burst (excludes burst drain and burst overtime accepts). */
export function sumPreMeterBurstMeterEarned(
  acceptedWordTimeline: readonly BoggleCompletedRunAcceptedWordFact[],
): number {
  return sumAcceptedWordFacts(acceptedWordTimeline, word =>
    isMeterGainExcludedForMeterBurst(word) ? 0 : word.meterGain,
  )
}

export function sumPreMeterBurstOvermeterEarned(
  acceptedWordTimeline: readonly BoggleCompletedRunAcceptedWordFact[],
): number {
  return sumAcceptedWordFacts(acceptedWordTimeline, word =>
    isMeterGainExcludedForMeterBurst(word) ? 0 : word.overmeterGain,
  )
}

/** @deprecated Use `sumPreMeterBurstMeterEarned`. */
export const sumPreGoldRushMeterEarned = sumPreMeterBurstMeterEarned

/** @deprecated Use `sumPreMeterBurstOvermeterEarned`. */
export const sumPreGoldRushOvermeterEarned = sumPreMeterBurstOvermeterEarned

function assertSubmittedWordsMatchAcceptedWordTimeline(
  submittedWords: readonly string[],
  acceptedWordTimeline: readonly BoggleCompletedRunAcceptedWordFact[],
) {
  const acceptedWords = acceptedWordTimeline.map(word => word.word)
  if (
    submittedWords.length !== acceptedWords.length ||
    submittedWords.some((word, index) => word !== acceptedWords[index])
  ) {
    throw new Error(
      'Completed Boggle run submittedWords must match acceptedWordTimeline word order.',
    )
  }
}

function cloneJsonLike<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}
